import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, getSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Users, Calendar, Settings, Megaphone, Download, KeyRound,
  Plus, Trash2, Save, Sparkles, ShieldCheck, RefreshCw, Printer, Copy, Wifi,
  Star, ChevronDown, ChevronRight, TrendingUp, TrendingDown, MessageSquare, Heart,
} from "lucide-react";
import SmsAdminPanel from "@/components/admin/SmsAdminPanel";
import BroadcastPanel from "@/components/admin/BroadcastPanel";
import ProgramLibraryAdmin from "@/components/admin/ProgramLibraryAdmin";
import ErrorBoundary from "@/components/ErrorBoundary";
import { TestModePanel, HostsPanel, SiteModePanel, FollowupPanel, ProfilesPanel, ReportsPanel, VolunteersPanel, EarlyAccessPanel } from "@/components/admin/EventHubV2Panels";

// ─── Shared helpers ──────────────────────────────────────────────────────────
const CATEGORIES = [
  "Guest", "Honoree", "Sponsor", "Speaker", "Panelist", "Trailblazer",
  "Volunteer", "Board Member", "Vendor", "Staff", "Media", "Other",
];

const EVENT_DAY_FRIDAY = "2026-07-17";
const EVENT_DAY_SATURDAY = "2026-07-18";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

// ─── Main tab entry ──────────────────────────────────────────────────────────
export default function EventHubAdminTab() {
  const [sub, setSub] = useState("overview");
  const tabs = [
    ["overview", "Overview"],
    ["testmode", "Test Mode"],
    ["hosts", "Hosts"],
    ["attendees", "Master Guest List"],
    ["program", "Program Library"],
    ["profiles", "Profiles"],
    ["volunteers", "Volunteers"],
    ["earlyaccess", "2027 Early Access"],
    ["sitemode", "Site Mode"],
    ["followups", "Follow-Ups"],
    ["survey", "Survey"],
    ["reports", "Reports"],
    ["settings", "Settings"],
    ["announcements", "Announcements"],
    ["analytics", "Analytics"],
  ];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l]) => (
          <button
            key={k}
            data-testid={`eventhub-subtab-${k}`}
            onClick={() => setSub(k)}
            className={`text-xs font-[Outfit] px-3 py-1.5 rounded-full border ${
              sub === k
                ? "bg-fuchsia-600 text-white border-fuchsia-600"
                : "bg-white text-emerald-900 border-emerald-200 hover:border-fuchsia-300"
            }`}
          >{l}</button>
        ))}
      </div>
      {sub === "overview" && <ErrorBoundary label="Overview"><OverviewSub /></ErrorBoundary>}
      {sub === "testmode" && <ErrorBoundary label="Test Mode"><TestModePanel /></ErrorBoundary>}
      {sub === "hosts" && <ErrorBoundary label="Hosts"><HostsPanel /></ErrorBoundary>}
      {sub === "attendees" && <ErrorBoundary label="Attendees"><AttendeesSub /></ErrorBoundary>}
      {sub === "program" && <ErrorBoundary label="Program Library"><ProgramLibraryAdmin /></ErrorBoundary>}
      {sub === "profiles" && <ErrorBoundary label="Profiles"><ProfilesPanel /></ErrorBoundary>}
      {sub === "volunteers" && <ErrorBoundary label="Volunteers"><VolunteersPanel /></ErrorBoundary>}
      {sub === "earlyaccess" && <ErrorBoundary label="2027 Early Access"><EarlyAccessPanel /></ErrorBoundary>}
      {sub === "sitemode" && <ErrorBoundary label="Site Mode"><SiteModePanel /></ErrorBoundary>}
      {sub === "followups" && <ErrorBoundary label="Follow-Ups"><FollowupPanel /></ErrorBoundary>}
      {sub === "survey" && <ErrorBoundary label="Survey"><SurveySub /></ErrorBoundary>}
      {sub === "reports" && <ErrorBoundary label="Reports"><ReportsPanel /></ErrorBoundary>}
      {sub === "settings" && <ErrorBoundary label="Settings"><SettingsSub /></ErrorBoundary>}
      {sub === "announcements" && <ErrorBoundary label="Announcements"><AnnouncementsSub /></ErrorBoundary>}
      {sub === "analytics" && <ErrorBoundary label="Analytics"><AnalyticsSub /></ErrorBoundary>}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────
function OverviewSub() {
  const [stats, setStats] = useState(null);
  const [pinStatus, setPinStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/event/volunteer/stats"),
        api.get("/event/admin/pin/status"),
      ]);
      setStats(s.data);
      setPinStatus(p.data);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotatePin = async () => {
    const newPin = prompt("Enter a new event PIN (min 4 chars):");
    if (!newPin) return;
    try {
      await api.post("/event/admin/pin/rotate", { new_pin: newPin });
      toast.success("PIN rotated. All volunteer sessions were invalidated.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to rotate PIN");
    }
  };

  const seedDemo = async () => {
    if (!window.confirm("Add 8 demo attendees (labeled 'DEMO')?")) return;
    try {
      const { data } = await api.post("/event/admin/seed-demo");
      toast.success(`Inserted ${data.inserted} demo attendees.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Seed failed"); }
  };

  const deleteDemo = async () => {
    if (!window.confirm("Delete all demo attendees and their check-ins?")) return;
    try {
      const { data } = await api.delete("/event/admin/seed-demo");
      toast.success(`Deleted ${data.deleted} demo records.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const purgeTestData = async () => {
    let preview;
    try {
      const { data } = await api.get("/event/admin/test-data/preview");
      preview = data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      return;
    }
    if (!preview.count) {
      toast.success("No test data found — you're already clean.");
      return;
    }
    const sample = (preview.sample || [])
      .slice(0, 8)
      .map((a) => `• ${a.first_name} ${a.last_name} <${a.email || "—"}>`)
      .join("\n");
    const msg = `This will DELETE ${preview.count} attendee(s) matching test-data patterns (DEMO/TEST names, @example.com emails, is_demo/is_test flags) and every one of their check-ins and SMS logs.\n\nSample of what will be removed:\n${sample}\n\nContinue?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete("/event/admin/test-data/purge");
      toast.success(
        `Purge complete · ${data.deleted_attendees} attendees, ${data.deleted_check_ins} check-ins, ${data.deleted_sms_logs} SMS logs removed.`
      );
    } catch (err) { toast.error(err?.response?.data?.detail || "Purge failed"); }
  };

  if (loading) return <div className="p-8 text-center text-emerald-800/60"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        <MetricCard label="Registered" value={stats?.total_registered ?? "—"} />
        <MetricCard label="Checked In" value={stats?.total_checked_in ?? "—"} />
        <MetricCard label="Friday" value={stats?.friday_checked_in ?? "—"} />
        <MetricCard label="Saturday" value={stats?.saturday_checked_in ?? "—"} />
        <MetricCard label="Walk-Ins" value={stats?.walk_ins ?? "—"} />
        <MetricCard label="% Checked" value={stats?.percentage_checked_in != null ? `${stats.percentage_checked_in}%` : "—"} />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-fuchsia-700" />
            <h3 className="font-[Cormorant_Garamond] text-xl">Volunteer PIN</h3>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-sm font-[Outfit] text-emerald-900">
            {pinStatus?.pin_set ? "PIN is active and hashed at rest." : "PIN not yet set."}
          </div>
          <div className="text-xs text-emerald-800/60 font-[Outfit]">
            {pinStatus?.pin_rotated_at
              ? `Last rotated: ${fmt(pinStatus.pin_rotated_at)}`
              : "Using default PIN. We recommend rotating before event day."}
            &nbsp;· Active sessions: {pinStatus?.active_sessions ?? 0}
          </div>
          <Button
            data-testid="eventhub-rotate-pin"
            onClick={rotatePin}
            className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9"
          >
            <RefreshCw size={14} className="mr-1.5" /> Rotate PIN
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-fuchsia-700" />
            <h3 className="font-[Cormorant_Garamond] text-xl">Demo & Test Data</h3>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button data-testid="eventhub-seed-demo" onClick={seedDemo} variant="outline" className="rounded-full text-xs h-9">
            <Plus size={14} className="mr-1.5" /> Seed 8 demo attendees
          </Button>
          <Button data-testid="eventhub-delete-demo" onClick={deleteDemo} variant="outline" className="rounded-full text-xs h-9 text-fuchsia-700 border-fuchsia-300">
            <Trash2 size={14} className="mr-1.5" /> Remove seed demos
          </Button>
          <Button data-testid="eventhub-purge-test-data" onClick={purgeTestData} variant="outline" className="rounded-full text-xs h-9 text-white bg-fuchsia-700 hover:bg-fuchsia-800 border-fuchsia-800">
            <Trash2 size={14} className="mr-1.5" /> Purge ALL test data
          </Button>
        </CardContent>
      </Card>

      <VolunteerAccessCard pinStatus={pinStatus} />

      {/* Twilio SMS status + test-send + activity log */}
      <ErrorBoundary label="SMS panel">
        <SmsAdminPanel />
      </ErrorBoundary>

      {/* Ad-hoc broadcast SMS (post-event photo reminders, save-the-date, etc.) */}
      <ErrorBoundary label="Broadcast panel">
        <BroadcastPanel />
      </ErrorBoundary>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-3">
      <div className="text-[10px] font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60">{label}</div>
      <div className="mt-1 font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950">{value}</div>
    </div>
  );
}

// ─── Volunteer Access Card (printable one-pager) ────────────────────────────
function VolunteerAccessCard({ pinStatus }) {
  const [showPin, setShowPin] = useState(false);
  const [customPin, setCustomPin] = useState("");
  const origin = typeof window !== "undefined" ? window.location.origin : "https://pitch.businessshowers.org";
  const loginUrl = `${origin}/event/volunteer-login`;
  const dashboardUrl = `${origin}/event/volunteer-dashboard`;
  const defaultPin = "FIMBS26";
  const pinToShow = customPin || defaultPin;

  const copyText = async (t, label) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed — long-press to select instead."); }
  };

  const openOnePager = () => {
    const html = renderVolunteerOnePager({ loginUrl, pin: pinToShow });
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked. Please allow popups for this page."); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <Card data-testid="volunteer-access-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-fuchsia-700" />
          <h3 className="font-[Cormorant_Garamond] text-xl">Volunteer Access</h3>
        </div>
        <p className="text-xs font-[Outfit] text-emerald-800/60 mt-1">
          One shared PIN unlocks the volunteer check-in dashboard for 12 hours per session. Print the one-pager for training day.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/40 p-4 space-y-2">
          <FieldRow label="Login URL" value={loginUrl} onCopy={() => copyText(loginUrl, "URL")} testid="vol-copy-url" />
          <FieldRow
            label="PIN"
            value={showPin ? pinToShow : "••••••"}
            onCopy={() => copyText(pinToShow, "PIN")}
            testid="vol-copy-pin"
            action={
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                data-testid="vol-toggle-pin"
                className="text-[11px] font-[Outfit] font-semibold text-fuchsia-700 hover:text-fuchsia-900 underline"
              >
                {showPin ? "Hide" : "Show"}
              </button>
            }
          />
          <div className="text-[11px] font-[Outfit] text-emerald-800/60 pt-1">
            {pinStatus?.pin_rotated_at
              ? `Custom PIN active · last rotated ${fmt(pinStatus.pin_rotated_at)}`
              : "No custom PIN set yet — the default above is in effect."}
          </div>
        </div>

        <div className="rounded-2xl border-2 border-fuchsia-200 bg-white p-4">
          <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 mb-2">Quick instructions</div>
          <ol className="text-xs font-[Outfit] text-emerald-950/80 space-y-1.5 list-decimal list-outside pl-5">
            <li>Open <span className="font-mono text-[11px] bg-emerald-50 px-1.5 py-0.5 rounded">{loginUrl.replace(/^https?:\/\//, "")}</span> on any phone or tablet.</li>
            <li>Enter your first name (identifies you in the audit log) and the PIN above.</li>
            <li>The dashboard shows every attendee. Search by name, email, or phone.</li>
            <li>Tap a name → confirm the day → hit <strong>Check In</strong>. Live counter updates on every volunteer&apos;s screen instantly.</li>
            <li>For walk-ins, tap <strong>+ Walk-In</strong> and enter the guest&apos;s info — they&apos;ll be added to the master list and checked in in one step.</li>
            <li>Session lasts 12 hours. Log out from the top-right menu when your shift ends.</li>
          </ol>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            onClick={openOnePager}
            data-testid="vol-open-onepager"
            className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9"
          >
            <Printer size={14} className="mr-1.5" /> Open printable one-pager
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => copyText(`${loginUrl}\nPIN: ${pinToShow}`, "URL + PIN")}
            data-testid="vol-copy-bundle"
            className="rounded-full text-xs h-9"
          >
            <Copy size={14} className="mr-1.5" /> Copy URL + PIN
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FieldRow({ label, value, onCopy, action = null, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] tracking-widest uppercase font-[Outfit] font-bold text-emerald-800/60 min-w-[70px] shrink-0">{label}</div>
      <div className="flex-1 min-w-0 font-mono text-[13px] text-emerald-950 truncate">{value}</div>
      {action}
      <button
        type="button"
        onClick={onCopy}
        data-testid={testid}
        aria-label={`Copy ${label}`}
        className="inline-flex items-center gap-1 rounded-full h-7 px-2 bg-white border border-emerald-200 text-emerald-800 hover:border-fuchsia-400 hover:text-fuchsia-700 text-[11px] font-[Outfit] font-semibold shrink-0"
      >
        <Copy size={11} /> Copy
      </button>
    </div>
  );
}

function renderVolunteerOnePager({ loginUrl, pin }) {
  return `<!doctype html><html><head><meta charset="utf-8">
<title>Volunteer Check-In · Business Showers 2026</title>
<style>
  @page { size: letter; margin: 0.5in; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0f172a; margin: 0; padding: 32px; background: #ffffff; }
  h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 36px; margin: 0 0 4px; color: #052e16; }
  .eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; color: #A50E52; font-weight: 800; margin-bottom: 12px; }
  .card { border: 2px solid #A50E52; border-radius: 20px; padding: 18px 22px; margin: 16px 0; background: #fff5fa; }
  .card h2 { font-family: 'Cormorant Garamond', Georgia, serif; margin: 0 0 6px; font-size: 22px; color: #052e16; }
  .field { display: flex; gap: 12px; align-items: baseline; margin: 6px 0; }
  .field .label { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 800; color: #A50E52; min-width: 70px; }
  .field .value { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 15px; color: #052e16; word-break: break-all; }
  .pin-hero { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 44px; letter-spacing: 6px; color: #A50E52; font-weight: 800; text-align: center; padding: 12px 0; border-top: 2px dashed #A50E52; border-bottom: 2px dashed #A50E52; margin: 8px 0; }
  ol { padding-left: 20px; font-size: 13.5px; line-height: 1.55; color: #052e16; }
  ol li { margin: 6px 0; }
  .footer { font-size: 10.5px; color: #64748b; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; }
  .divider { height: 1px; background: #e2e8f0; margin: 20px 0; }
  .highlight { background: #fce7f3; padding: 1px 6px; border-radius: 4px; font-weight: 600; color: #A50E52; }
  @media print { .no-print { display: none; } body { padding: 0; } }
</style></head><body>
  <div class="eyebrow">Volunteer Access Card · July 17 & 18, 2026</div>
  <h1>Business Showers Check-In</h1>
  <p style="margin: 0 0 4px; color:#475569; font-size:13px;">Keep this card with you throughout the event weekend. One page. Everything you need.</p>

  <div class="card">
    <h2>Your login details</h2>
    <div class="field"><span class="label">URL</span><span class="value">${loginUrl}</span></div>
    <div class="pin-hero">${pin}</div>
    <div class="field"><span class="label">PIN</span><span class="value">${pin} — enter after opening the URL above</span></div>
    <div class="field"><span class="label">Session</span><span class="value">Stays signed in for 12 hours per shift</span></div>
  </div>

  <div class="divider"></div>

  <h2 style="font-family:'Cormorant Garamond',Georgia,serif; margin: 0 0 8px;">How to check a guest in</h2>
  <ol>
    <li>Open the URL above on any phone or tablet. (Add to home screen for one-tap access.)</li>
    <li>Enter <span class="highlight">your first name</span> (this shows in the audit log so credit is assigned to you) and the <span class="highlight">PIN</span>.</li>
    <li>Search the guest by <span class="highlight">name, email, or mobile number</span>. Registered guests appear instantly.</li>
    <li>Tap the guest → confirm <span class="highlight">Friday · July 17</span> or <span class="highlight">Saturday · July 18</span> → hit the pink <strong>Check In</strong> button.</li>
    <li>The moment you tap Check In, every other volunteer's dashboard updates too. No refresh needed.</li>
    <li>For walk-ins, tap <strong>+ Walk-In</strong>, enter their name, email and mobile, and check them in — they're added to the master list automatically.</li>
    <li>Not sure? Tap <strong>Attending both nights?</strong> for the group-of-host flow, or send the guest to the self check-in QR at the door.</li>
  </ol>

  <h2 style="font-family:'Cormorant Garamond',Georgia,serif; margin: 20px 0 8px;">Troubleshooting</h2>
  <ol>
    <li><strong>Search turns up nothing?</strong> Try just the first name, or the mobile digits without punctuation.</li>
    <li><strong>Guest says "I'm the guest of a host"?</strong> Tap <strong>Guest of Host</strong>, choose the host name, then check them in.</li>
    <li><strong>Check-In button greyed out?</strong> Check-in only opens 2 hours before the event start time. Confirm the day.</li>
    <li><strong>Dashboard offline?</strong> Refresh the page; your PIN session persists.</li>
  </ol>

  <div class="footer">
    Freedom In Me Business Showers · Fast Forward · 2026<br>
    Questions during the event? Ask any admin on-site or call the event director.
  </div>

  <div class="no-print" style="text-align:center; margin-top:16px;">
    <button onclick="window.print()" style="background:#A50E52; color:white; border:none; padding:10px 20px; border-radius:9999px; font-size:13px; font-weight:700; cursor:pointer;">Print this one-pager</button>
  </div>
</body></html>`;
}


// ─── Attendees ──────────────────────────────────────────────────────────────
function AttendeesSub() {
  const [q, setQ] = useState("");
  const [walkOnly, setWalkOnly] = useState(false);
  const [dayFilter, setDayFilter] = useState("");        // "" | "friday" | "saturday"
  const [statusFilter, setStatusFilter] = useState("");  // "" | "checked_in_friday" | "checked_in_saturday" | "checked_in_both" | "not_checked_in"
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // attendee_id → bool (survey row)
  const [vipStatus, setVipStatus] = useState({});  // Friday VIP seed batch status
  const [satStatus, setSatStatus] = useState({});  // Saturday seed batch status
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/admin/attendees", {
        params: {
          q: q || undefined,
          walk_in: walkOnly ? true : undefined,
          day: dayFilter || undefined,
          status: statusFilter || undefined,
        },
      });
      setItems(data.attendees || []);
      setCounts(data.day_counts || {});
    } catch { /* noop */ }
    setLoading(false);
  }, [q, walkOnly, dayFilter, statusFilter]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(load, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [load]);

  const loadVipStatus = useCallback(async () => {
    try {
      const [{ data: fri }, { data: sat }] = await Promise.all([
        api.get("/event/admin/attendees/friday-vip-status"),
        api.get("/event/admin/attendees/saturday-status"),
      ]);
      setVipStatus(fri);
      setSatStatus(sat);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadVipStatus(); }, [loadVipStatus]);

  const importFridayVip = async () => {
    if (vipStatus.already_imported) {
      const ok = window.confirm(
        `The Friday VIP list is already imported (${vipStatus.existing_count} attendees).\n\nTo re-import, first delete the batch. Delete now?`
      );
      if (!ok) return;
      try {
        const { data } = await api.delete("/event/admin/attendees/import-friday-vip");
        toast.success(`Deleted ${data.deleted_attendees} attendees + ${data.deleted_check_ins} check-ins.`);
        await loadVipStatus();
        await load();
      } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
      return;
    }
    if (!window.confirm(`Import ${vipStatus.batch_size || "?"} attendees from the Friday VIP list?\n\nSafe to run — the import is idempotent. Duplicates (host ticket allocations) are preserved.`)) return;
    try {
      const { data } = await api.post("/event/admin/attendees/import-friday-vip");
      if (data.already_imported) {
        toast.info(`Already imported (${data.existing_count} attendees present).`);
      } else {
        toast.success(`✓ Imported ${data.inserted} Friday VIP attendees.`);
      }
      await loadVipStatus();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
  };

  const importSaturday = async () => {
    if (satStatus.already_imported) {
      const ok = window.confirm(
        `The Saturday guest list is already imported (${satStatus.existing_count} attendees).\n\nTo re-import, first delete the batch. Delete now?`
      );
      if (!ok) return;
      try {
        const { data } = await api.delete("/event/admin/attendees/import-saturday");
        toast.success(`Deleted ${data.deleted_attendees} attendees + ${data.deleted_check_ins} check-ins.`);
        await loadVipStatus();
        await load();
      } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
      return;
    }
    if (!window.confirm(`Import ${satStatus.batch_size || "?"} attendees from the Saturday guest list?\n\nSafe to run — the import is idempotent. Host ticket duplicates are numbered (e.g. Matt Winston Guest 1-9).`)) return;
    try {
      const { data } = await api.post("/event/admin/attendees/import-saturday");
      if (data.already_imported) {
        toast.info(`Already imported (${data.existing_count} attendees present).`);
      } else {
        toast.success(`✓ Imported ${data.inserted} Saturday attendees.`);
      }
      await loadVipStatus();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
  };

  const csvUrl = `${API_BASE}/event/admin/attendees.csv`;
  const downloadCsv = async () => {
    try {
      const res = await fetch(csvUrl, { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `master_guest_list_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
  };

  const STATUS_CHIPS = [
    ["", "All"],
    ["checked_in_friday", `Fri only (${counts.friday || 0})`],
    ["checked_in_saturday", `Sat only (${counts.saturday || 0})`],
    ["checked_in_both", `Both days (${counts.both || 0})`],
    ["not_checked_in", `Not yet (${counts.not_checked_in || 0})`],
  ];

  return (
    <div>
      <div className="mb-3 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-[Outfit] font-bold text-fuchsia-700 mb-1">Master Guest List</div>
        <div className="text-xs font-[Outfit] text-emerald-950/70">
          Single source of truth for every registrant. Any edit here flows into check-in immediately —
          there are no separate attendee records. Attendance is recorded per day: a Friday check-in never
          auto-checks a person into Saturday.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input
          data-testid="eventhub-attendees-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, org…"
          className="max-w-xs h-10 rounded-full font-[Outfit]"
        />
        <select
          data-testid="eventhub-attendees-dayfilter"
          value={dayFilter}
          onChange={(e) => setDayFilter(e.target.value)}
          className="h-10 rounded-md border-2 border-emerald-100 bg-white px-2 text-xs font-[Outfit]"
        >
          <option value="">Registered any day</option>
          <option value="friday">Registered Friday</option>
          <option value="saturday">Registered Saturday</option>
        </select>
        <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
          <input
            data-testid="eventhub-attendees-walkonly"
            type="checkbox"
            checked={walkOnly}
            onChange={(e) => setWalkOnly(e.target.checked)}
            className="h-4 w-4 rounded"
          />
          Walk-ins only
        </label>
        <Button data-testid="eventhub-attendees-csv" onClick={downloadCsv} className="ml-auto rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9">
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
        <Button
          data-testid="eventhub-import-friday-vip"
          onClick={importFridayVip}
          className={`rounded-full text-xs h-9 ${vipStatus.already_imported ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-900 border border-emerald-300" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"}`}
          title={vipStatus.already_imported ? `Already imported (${vipStatus.existing_count})` : `Insert the ${vipStatus.batch_size || 82}-attendee Friday VIP list`}
        >
          {vipStatus.already_imported
            ? <>✓ Friday VIP · {vipStatus.existing_count}</>
            : <><Plus size={14} className="mr-1.5" /> Import Friday VIP List ({vipStatus.batch_size || 82})</>}
        </Button>
        <Button
          data-testid="eventhub-import-saturday"
          onClick={importSaturday}
          className={`rounded-full text-xs h-9 ${satStatus.already_imported ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-900 border border-emerald-300" : "bg-emerald-700 hover:bg-emerald-800 text-white"}`}
          title={satStatus.already_imported ? `Already imported (${satStatus.existing_count})` : `Insert the ${satStatus.batch_size || 95}-attendee Saturday guest list`}
        >
          {satStatus.already_imported
            ? <>✓ Saturday · {satStatus.existing_count}</>
            : <><Plus size={14} className="mr-1.5" /> Import Saturday List ({satStatus.batch_size || 95})</>}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_CHIPS.map(([k, l]) => (
          <button
            key={k || "all"}
            data-testid={`eventhub-attendees-status-${k || "all"}`}
            onClick={() => setStatusFilter(k)}
            className={`text-[10px] font-[Outfit] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              statusFilter === k ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-emerald-200 text-emerald-900"
            }`}
          >{l}</button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No attendees found.</div>
          ) : (
            <table className="w-full text-xs font-[Outfit]">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Org</th>
                  <th className="text-center px-2 py-2">Day 1 · Fri</th>
                  <th className="text-center px-2 py-2">Day 2 · Sat</th>
                  <th className="text-center px-2 py-2">Feedback</th>
                  <th className="text-left px-3 py-2">Registered</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const st = a.check_in_status || {};
                  const s = a.survey;
                  const isOpen = !!expanded[a.id];
                  return (
                  <React.Fragment key={a.id}>
                  <tr data-testid={`eventhub-attendee-${a.id}`} className="border-t border-emerald-100">
                    <td className="px-3 py-2 font-semibold text-emerald-950">
                      {a.first_name} {a.last_name}
                      {a.is_test && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase">test</span>}
                      {a.walk_in && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase">walk-in</span>}
                    </td>
                    <td className="px-3 py-2">{a.email}</td>
                    <td className="px-3 py-2">{a.phone}</td>
                    <td className="px-3 py-2">{a.organization || "—"}</td>
                    <td className="px-2 py-2 text-center" data-testid={`attendee-${a.id}-friday`}>
                      {st.friday ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[10px]">✓ IN</span>
                      ) : (
                        <span className="text-emerald-950/30">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center" data-testid={`attendee-${a.id}-saturday`}>
                      {st.saturday ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[10px]">✓ IN</span>
                      ) : (
                        <span className="text-emerald-950/30">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center" data-testid={`attendee-${a.id}-survey`}>
                      {s ? (
                        <button
                          type="button"
                          onClick={() => setExpanded((prev) => ({ ...prev, [a.id]: !prev[a.id] }))}
                          data-testid={`attendee-${a.id}-survey-toggle`}
                          className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 border border-fuchsia-300 px-2 py-1 hover:bg-fuchsia-100 transition"
                          title="View feedback"
                        >
                          <StarCluster n={s.overall} />
                          <span className="text-[10px] font-bold text-fuchsia-800">NPS {s.nps}</span>
                          {isOpen ? <ChevronDown size={12} className="text-fuchsia-700" /> : <ChevronRight size={12} className="text-fuchsia-700" />}
                        </button>
                      ) : (
                        <span className="text-emerald-950/30 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-emerald-950/60">{(a.created_at || "").split("T")[0]}</td>
                  </tr>
                  {isOpen && s && (
                    <tr key={`${a.id}-survey`} className="bg-fuchsia-50/40 border-t border-fuchsia-100" data-testid={`attendee-${a.id}-survey-expanded`}>
                      <td colSpan={8} className="px-4 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <SurveyStat label="Overall" value={<StarCluster n={s.overall} />} />
                          <SurveyStat label="Program" value={<StarCluster n={s.program} />} />
                          <SurveyStat label="NPS" value={<NpsPill n={s.nps} />} />
                        </div>
                        {(s.favorite || s.improvements) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            {s.favorite && (
                              <div>
                                <div className="text-[10px] tracking-widest uppercase font-bold text-fuchsia-700 mb-1">Favorite moment</div>
                                <div className="text-xs text-emerald-950 bg-white rounded-lg p-2 border border-fuchsia-100">&quot;{s.favorite}&quot;</div>
                              </div>
                            )}
                            {s.improvements && (
                              <div>
                                <div className="text-[10px] tracking-widest uppercase font-bold text-fuchsia-700 mb-1">Improvements</div>
                                <div className="text-xs text-emerald-950 bg-white rounded-lg p-2 border border-fuchsia-100">&quot;{s.improvements}&quot;</div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-2 text-[10px] font-[Outfit] font-semibold uppercase tracking-widest text-emerald-950/50">
                          <span>Submitted {(s.submitted_at || "").split("T")[0]}</span>
                          {s.consent_followup && (
                            <span className="inline-flex items-center gap-1 text-fuchsia-700"><Heart size={10} /> Open to follow-up</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Program CRUD ────────────────────────────────────────────────────────────
function ProgramSub() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [day, setDay] = useState(EVENT_DAY_FRIDAY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/program", { params: { event_day: day } });
      setEntries(data.entries || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const body = {
      event_day: editing.event_day,
      start_time: editing.start_time,
      end_time: editing.end_time,
      title: editing.title,
      speaker: editing.speaker || "",
      description: editing.description || "",
      location: editing.location || "",
      sponsor: editing.sponsor || "",
      image_url: editing.image_url || "",
      order: Number(editing.order) || 0,
    };
    try {
      if (editing.id) await api.patch(`/event/admin/program/${editing.id}`, body);
      else await api.post("/event/admin/program", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this program entry?")) return;
    try {
      await api.delete(`/event/admin/program/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {[
            [EVENT_DAY_FRIDAY, "Friday · Jul 17"],
            [EVENT_DAY_SATURDAY, "Saturday · Jul 18"],
          ].map(([v, l]) => (
            <button
              key={v}
              data-testid={`eventhub-program-day-${v}`}
              onClick={() => setDay(v)}
              className={`text-xs font-[Outfit] px-3 py-1.5 rounded-full ${day === v ? "bg-emerald-950 text-white" : "bg-white border border-emerald-200 text-emerald-900"}`}
            >{l}</button>
          ))}
        </div>
        <Button
          data-testid="eventhub-program-add"
          onClick={() => setEditing({ event_day: day, start_time: "13:00", end_time: "13:30", title: "", speaker: "", description: "", location: "", sponsor: "", image_url: "", order: (entries.length || 0) + 1 })}
          className="ml-auto rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-9"
        >
          <Plus size={14} className="mr-1.5" /> Add Program Item
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <h3 className="font-[Cormorant_Garamond] text-xl">{editing.id ? "Edit" : "New"} program item</h3>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid grid-cols-2 gap-2 text-sm">
              <label className="col-span-2">
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Title *</span>
                <Input data-testid="program-form-title" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Event Day</span>
                <select value={editing.event_day} onChange={(e) => setEditing({ ...editing, event_day: e.target.value })} className="h-10 w-full rounded-md border-2 border-emerald-100 px-3 text-sm">
                  <option value={EVENT_DAY_FRIDAY}>Friday · Jul 17</option>
                  <option value={EVENT_DAY_SATURDAY}>Saturday · Jul 18</option>
                </select>
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Order</span>
                <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Start (HH:MM 24h ET)</span>
                <Input data-testid="program-form-start" required value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} placeholder="13:00" />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">End (HH:MM 24h ET)</span>
                <Input data-testid="program-form-end" required value={editing.end_time} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} placeholder="14:00" />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Speaker</span>
                <Input value={editing.speaker} onChange={(e) => setEditing({ ...editing, speaker: e.target.value })} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Location</span>
                <Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Sponsor</span>
                <Input value={editing.sponsor} onChange={(e) => setEditing({ ...editing, sponsor: e.target.value })} />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Image URL</span>
                <Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
              </label>
              <label className="col-span-2">
                <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Description</span>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} />
              </label>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="program-form-save" className="bg-emerald-950 hover:bg-emerald-900 text-white"><Save size={14} className="mr-1.5" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No program items yet.</div>
          ) : (
            <ul className="divide-y divide-emerald-100">
              {entries.map((e) => (
                <li key={e.id} data-testid={`program-row-${e.id}`} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-20 text-right text-xs font-[Outfit] font-bold text-emerald-950">
                    {e.start_time}<div className="text-emerald-800/50 font-normal">→ {e.end_time}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">{e.title}</div>
                    {e.speaker && <div className="text-xs text-fuchsia-800 font-[Outfit] font-semibold">{e.speaker}</div>}
                    <div className="text-[11px] font-[Outfit] text-emerald-800/60">{e.location}{e.sponsor && ` · Sponsor: ${e.sponsor}`}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(e)} className="p-1.5 rounded hover:bg-emerald-50" title="Edit"><Settings size={14} /></button>
                    <button onClick={() => del(e.id)} className="p-1.5 rounded hover:bg-fuchsia-50 text-fuchsia-700" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Settings (donation, photo, social, momentum) ───────────────────────────
function SettingsSub() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event/admin/settings");
      setSettings(data);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/event/admin/settings", settings);
      toast.success("Settings saved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (!settings) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  const upd = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const updCard = (idx, k, v) => {
    const cards = [...(settings.action_cards || [])];
    cards[idx] = { ...cards[idx], [k]: v };
    upd("action_cards", cards);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="eventhub-settings-save" onClick={save} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
          {saving ? <Loader2 className="mr-1.5 animate-spin" size={14} /> : <Save size={14} className="mr-1.5" />}
          Save all settings
        </Button>
      </div>

      <SettingsGroup title="Homepage & Event Dates">
        <TxtField label="Homepage announcement (top banner)" value={settings.homepage_announcement || ""} onChange={(v) => upd("homepage_announcement", v)} />
        <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
          <input
            data-testid="settings-checkin-enabled"
            type="checkbox"
            checked={!!settings.check_in_enabled}
            onChange={(e) => upd("check_in_enabled", e.target.checked)}
            className="h-4 w-4 rounded"
          /> Check-in enabled (public flow)
        </label>
      </SettingsGroup>

      <SettingsGroup title="Donation" testid="settings-group-donation">
        <TxtField label="Donation URL" value={settings.donation_url || ""} onChange={(v) => upd("donation_url", v)} testid="settings-donation-url" />
        <TxtField label="Impact statement (shown next to Donate)" value={settings.donation_impact || ""} onChange={(v) => upd("donation_impact", v)} multiline />
        <TxtField label="Suggested amounts (comma-separated, USD)" value={(settings.donation_amounts || []).join(", ")} onChange={(v) => upd("donation_amounts", v.split(",").map((x) => Number(x.trim())).filter(Boolean))} />
      </SettingsGroup>

      <SettingsGroup title="Photo Integration" testid="settings-group-photos">
        <TxtField label="Platform name (Kululu, Guestpix, etc.)" value={settings.photo_platform_name || ""} onChange={(v) => upd("photo_platform_name", v)} />
        <TxtField label="Upload URL" value={settings.photo_upload_url || ""} onChange={(v) => upd("photo_upload_url", v)} testid="settings-photo-upload-url" />
        <TxtField label="Gallery URL" value={settings.photo_gallery_url || ""} onChange={(v) => upd("photo_gallery_url", v)} />
        <TxtField label="Live photo wall URL" value={settings.photo_live_wall_url || ""} onChange={(v) => upd("photo_live_wall_url", v)} />
        <TxtField label="Friday album URL" value={settings.photo_friday_album_url || ""} onChange={(v) => upd("photo_friday_album_url", v)} />
        <TxtField label="Saturday album URL" value={settings.photo_saturday_album_url || ""} onChange={(v) => upd("photo_saturday_album_url", v)} />
        <TxtField label="QR code image URL" value={settings.photo_qr_image_url || ""} onChange={(v) => upd("photo_qr_image_url", v)} />
        <TxtField label="Photo/video permission notice" value={settings.photo_permission_notice || ""} onChange={(v) => upd("photo_permission_notice", v)} multiline />
      </SettingsGroup>

      <SettingsGroup title="Social Media">
        <TxtField label="Facebook URL" value={settings.social_facebook || ""} onChange={(v) => upd("social_facebook", v)} testid="settings-social-facebook" />
        <TxtField label="Instagram URL" value={settings.social_instagram || ""} onChange={(v) => upd("social_instagram", v)} />
        <TxtField label="LinkedIn (Business Showers Org)" value={settings.social_linkedin_org || ""} onChange={(v) => upd("social_linkedin_org", v)} />
        <TxtField label="LinkedIn (Donja Williams)" value={settings.social_linkedin_donja || ""} onChange={(v) => upd("social_linkedin_donja", v)} />
        <TxtField label="DonjaWilliams.com" value={settings.social_donja_website || ""} onChange={(v) => upd("social_donja_website", v)} />
      </SettingsGroup>

      <SettingsGroup title="Keep the Momentum Cards">
        <div className="space-y-3">
          {(settings.action_cards || []).map((c, i) => (
            <div key={c.id} data-testid={`settings-card-${c.id}`} className="rounded-xl border border-emerald-200 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-[Cormorant_Garamond] text-lg text-emerald-950">{c.title || "(untitled)"}</div>
                <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
                  <input
                    data-testid={`settings-card-active-${c.id}`}
                    type="checkbox"
                    checked={!!c.active}
                    onChange={(e) => updCard(i, "active", e.target.checked)}
                    className="h-4 w-4 rounded"
                  /> Active
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <label>
                  <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Title</span>
                  <Input value={c.title || ""} onChange={(e) => updCard(i, "title", e.target.value)} />
                </label>
                <label>
                  <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">CTA text</span>
                  <Input value={c.cta || ""} onChange={(e) => updCard(i, "cta", e.target.value)} />
                </label>
                <label className="col-span-full">
                  <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Description</span>
                  <Input value={c.description || ""} onChange={(e) => updCard(i, "description", e.target.value)} />
                </label>
                <label className="col-span-full">
                  <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">URL</span>
                  <Input value={c.url || ""} onChange={(e) => updCard(i, "url", e.target.value)} placeholder="https://…" />
                </label>
                <label>
                  <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">Order</span>
                  <Input type="number" value={c.order || 0} onChange={(e) => updCard(i, "order", Number(e.target.value) || 0)} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </SettingsGroup>
    </div>
  );
}

function SettingsGroup({ title, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">{title}</h3></CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function TxtField({ label, value, onChange, multiline, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60">{label}</span>
      {multiline ? (
        <Textarea data-testid={testid} rows={2} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
      ) : (
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
      )}
    </label>
  );
}

// ─── Announcements ──────────────────────────────────────────────────────────
function AnnouncementsSub() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event/admin/announcements");
      setItems(data.announcements || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault?.();
    const body = { title: editing.title, body: editing.body || "", active: editing.active !== false, publish_at: editing.publish_at || null, expires_at: editing.expires_at || null };
    try {
      if (editing.id) await api.patch(`/event/admin/announcements/${editing.id}`, body);
      else await api.post("/event/admin/announcements", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/event/admin/announcements/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="eventhub-ann-add"
          onClick={() => setEditing({ title: "", body: "", active: true, publish_at: "", expires_at: "" })}
          className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-9"
        ><Plus size={14} className="mr-1.5" /> New announcement</Button>
      </div>
      {editing && (
        <Card>
          <CardHeader><h3 className="font-[Cormorant_Garamond] text-xl">{editing.id ? "Edit" : "New"} announcement</h3></CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-2 text-sm">
              <TxtField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="ann-title" />
              <TxtField label="Body (optional detail)" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} multiline />
              <TxtField label="Publish at (ISO datetime, optional)" value={editing.publish_at || ""} onChange={(v) => setEditing({ ...editing, publish_at: v })} />
              <TxtField label="Expires at (ISO datetime, optional)" value={editing.expires_at || ""} onChange={(v) => setEditing({ ...editing, expires_at: v })} />
              <label className="text-xs font-[Outfit] flex items-center gap-1.5 text-emerald-900">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 rounded" /> Active
              </label>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="ann-save" className="bg-emerald-950 hover:bg-emerald-900 text-white">Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-emerald-800/60 font-[Outfit]">No announcements yet.</div>
          ) : (
            <ul className="divide-y divide-emerald-100">
              {items.map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-[Cormorant_Garamond] text-lg text-emerald-950">{a.title}</div>
                    {a.body && <div className="text-xs text-emerald-800/70 font-[Outfit]">{a.body}</div>}
                    <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/50 mt-1">
                      {a.active ? "Active" : "Inactive"}
                      {a.publish_at && ` · publish: ${fmt(a.publish_at)}`}
                      {a.expires_at && ` · expires: ${fmt(a.expires_at)}`}
                    </div>
                  </div>
                  <button onClick={() => setEditing(a)} className="p-1.5 rounded hover:bg-emerald-50"><Settings size={14} /></button>
                  <button onClick={() => del(a.id)} className="p-1.5 rounded hover:bg-fuchsia-50 text-fuchsia-700"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Survey (stats · response table · CSV export) ───────────────────────────
function SurveySub() {
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [vips, setVips] = useState([]);
  const [vipContacted, setVipContacted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes, vipRes] = await Promise.all([
        api.get("/event/admin/survey/stats"),
        api.get("/event/admin/survey/responses", { params: { limit: 100 } }),
        api.get("/event/admin/survey/vip-followups"),
      ]);
      setStats(statsRes.data);
      setResponses(listRes.data.responses || []);
      setVips(vipRes.data.vips || []);
      setVipContacted(vipRes.data.contacted || 0);
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markContacted = async (id, newStatus) => {
    try {
      await api.patch(`/event/admin/survey/${id}/followup`, { status: newStatus });
      // Optimistic update
      setVips((prev) => prev.map((v) => v.id === id ? { ...v, followup_status: newStatus, followup_contacted_at: newStatus === "contacted" ? new Date().toISOString() : v.followup_contacted_at } : v));
      setVipContacted((prev) => newStatus === "contacted" ? prev + 1 : Math.max(0, prev - 1));
      toast.success(newStatus === "contacted" ? "Marked as contacted" : "Reset to pending");
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const copyContact = async (v) => {
    const att = v.attendee || {};
    const name = `${att.first_name || v.first_name || ""} ${att.last_name || ""}`.trim();
    const lines = [name];
    if (att.email || v.email) lines.push(att.email || v.email);
    if (att.phone || v.phone) lines.push(att.phone || v.phone);
    try {
      await navigator.clipboard.writeText(lines.filter(Boolean).join("\n"));
      toast.success("Contact info copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/survey/export.csv`, {
        headers: { Authorization: `Bearer ${getSessionToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `business-showers-survey-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
  };

  if (loading) return <div className="text-sm text-emerald-800/60"><Loader2 className="inline animate-spin mr-2" size={14} />Loading survey data…</div>;

  const total = stats?.total || 0;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-3 flex items-start gap-3">
        <span className="inline-flex h-9 w-9 rounded-xl bg-fuchsia-600 text-white items-center justify-center shrink-0">
          <MessageSquare size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-[Outfit] font-bold text-fuchsia-700">Survey · /event/survey</div>
          <div className="text-xs font-[Outfit] text-emerald-950/70">
            Native 5-question survey + NPS scale. Responses link to attendee records via email/phone match.
          </div>
        </div>
        <Button
          data-testid="survey-export-csv"
          onClick={downloadCsv}
          disabled={!total}
          className="rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9 disabled:bg-emerald-200 disabled:text-emerald-800/40"
        >
          <Download size={14} className="mr-1.5" /> Export CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          data-testid="survey-refresh"
          className="rounded-full text-xs h-9"
        ><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      </div>

      {total === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-white p-10 text-center" data-testid="survey-empty">
          <div className="mx-auto h-12 w-12 rounded-full bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center mb-3"><MessageSquare size={20} /></div>
          <div className="font-[Cormorant_Garamond] text-2xl text-emerald-950 leading-tight">No survey responses yet</div>
          <div className="mt-1 text-xs font-[Outfit] text-emerald-950/60">
            Attendees are invited via the check-in SMS + morning-after SMS. Share <span className="font-mono bg-emerald-50 px-1.5 py-0.5 rounded">pitch.businessshowers.org/event/survey</span> to open manually.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCardBig label="Total responses" value={total} testid="stat-total" />
            <StatCardBig label="Avg overall" value={<span><StarCluster n={Math.round(stats.avg_overall)} /> <span className="ml-1 text-xs">{stats.avg_overall}</span></span>} testid="stat-overall" />
            <StatCardBig label="Avg program" value={<span><StarCluster n={Math.round(stats.avg_program)} /> <span className="ml-1 text-xs">{stats.avg_program}</span></span>} testid="stat-program" />
            <StatCardBig label="NPS score" value={<NpsBigPill nps={stats.nps} />} testid="stat-nps" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Promoters" value={stats.promoters} tint="emerald" icon={<TrendingUp size={12} />} testid="stat-promoters" />
            <MiniStat label="Passives" value={stats.passives} tint="amber" testid="stat-passives" />
            <MiniStat label="Detractors" value={stats.detractors} tint="fuchsia" icon={<TrendingDown size={12} />} testid="stat-detractors" />
          </div>

          {/* VIP Follow-Up Queue · 5-star fans + NPS 9-10 promoters who opted in */}
          <VipFollowupQueue vips={vips} contacted={vipContacted} onMarkContacted={markContacted} onCopyContact={copyContact} onRefresh={load} />

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-fuchsia-700" />
                <h3 className="font-[Cormorant_Garamond] text-xl">Recent responses <span className="text-sm text-emerald-800/60">({responses.length})</span></h3>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-emerald-50 text-emerald-800 uppercase text-[10px] tracking-widest">
                    <tr>
                      <th className="text-left px-3 py-2">Who</th>
                      <th className="text-center px-2 py-2">Overall</th>
                      <th className="text-center px-2 py-2">Program</th>
                      <th className="text-center px-2 py-2">NPS</th>
                      <th className="text-left px-3 py-2">Day</th>
                      <th className="text-left px-3 py-2">Submitted</th>
                      <th className="text-center px-2 py-2">Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((r) => {
                      const hasText = (r.favorite_moment || "").trim() || (r.improvements || "").trim();
                      const isOpen = !!expanded[r.id];
                      return (
                        <React.Fragment key={r.id}>
                          <tr className="border-t border-emerald-100" data-testid={`survey-row-${r.id}`}>
                            <td className="px-3 py-2 font-semibold text-emerald-950">
                              {r.first_name || <span className="italic text-emerald-950/50">Anonymous</span>}
                              {r.email && <div className="text-[10px] text-emerald-950/50 font-normal">{r.email}</div>}
                              {r.attendee_id && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase align-middle">linked</span>}
                              {r.consent_followup && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-800 font-bold uppercase align-middle">follow-up ok</span>}
                            </td>
                            <td className="px-2 py-2 text-center"><StarCluster n={r.overall_rating} /></td>
                            <td className="px-2 py-2 text-center"><StarCluster n={r.program_rating} /></td>
                            <td className="px-2 py-2 text-center"><NpsPill n={r.recommend_score} /></td>
                            <td className="px-3 py-2 text-emerald-950/70">{r.day || "—"}</td>
                            <td className="px-3 py-2 text-emerald-950/60">{(r.submitted_at || "").split("T")[0]}</td>
                            <td className="px-2 py-2 text-center">
                              {hasText ? (
                                <button
                                  type="button"
                                  onClick={() => setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))}
                                  data-testid={`survey-row-${r.id}-toggle`}
                                  className="inline-flex items-center gap-1 rounded-full bg-emerald-950 text-white px-2 py-1 hover:bg-emerald-900 text-[10px] font-bold uppercase tracking-widest"
                                >
                                  {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {isOpen ? "Hide" : "Read"}
                                </button>
                              ) : (
                                <span className="text-emerald-950/30 text-[11px]">—</span>
                              )}
                            </td>
                          </tr>
                          {isOpen && hasText && (
                            <tr className="bg-fuchsia-50/40 border-t border-fuchsia-100" data-testid={`survey-row-${r.id}-expanded`}>
                              <td colSpan={7} className="px-4 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {r.favorite_moment && (
                                    <div>
                                      <div className="text-[10px] tracking-widest uppercase font-bold text-fuchsia-700 mb-1">Favorite moment</div>
                                      <div className="text-xs text-emerald-950 bg-white rounded-lg p-2 border border-fuchsia-100">&quot;{r.favorite_moment}&quot;</div>
                                    </div>
                                  )}
                                  {r.improvements && (
                                    <div>
                                      <div className="text-[10px] tracking-widest uppercase font-bold text-fuchsia-700 mb-1">Improvements</div>
                                      <div className="text-xs text-emerald-950 bg-white rounded-lg p-2 border border-fuchsia-100">&quot;{r.improvements}&quot;</div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function StarCluster({ n = 0 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={12}
          fill={i <= n ? "#ED1C7A" : "transparent"}
          strokeWidth={1.5}
          className={i <= n ? "text-fuchsia-600" : "text-emerald-950/25"}
        />
      ))}
    </span>
  );
}

// ─── VIP Follow-Up Queue ─────────────────────────────────────────────────
function VipFollowupQueue({ vips = [], contacted = 0, onMarkContacted, onCopyContact, onRefresh }) {
  const [showContacted, setShowContacted] = useState(false);
  const [thanksModal, setThanksModal] = useState(null);   // { vip, body, can_send, reason, loading, sending }
  const filtered = showContacted ? vips : vips.filter((v) => v.followup_status !== "contacted");
  const pending = vips.length - contacted;

  const openThanksModal = async (vip) => {
    setThanksModal({ vip, loading: true });
    try {
      const { data } = await api.get(`/event/admin/survey/${vip.id}/thank-you-preview`);
      setThanksModal({
        vip,
        body: data.default_body,
        can_send: data.can_send,
        reason: data.reason,
        to_last4: data.to_last4,
        first_name: data.first_name,
        loading: false,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      setThanksModal(null);
    }
  };

  const sendThanks = async () => {
    if (!thanksModal?.can_send || thanksModal.sending) return;
    setThanksModal((s) => ({ ...s, sending: true }));
    try {
      const { data } = await api.post(`/event/admin/survey/${thanksModal.vip.id}/thank-you-sms`, {
        body: thanksModal.body,
      });
      if (data.ok) {
        toast.success(`Thank-you sent to ${thanksModal.first_name} — auto-marked as contacted.`);
        setThanksModal(null);
        if (onRefresh) await onRefresh();
      } else {
        toast.error(`Twilio: ${data.sms_status || data.sms_error || "failed"}`);
        setThanksModal((s) => ({ ...s, sending: false }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
      setThanksModal((s) => ({ ...s, sending: false }));
    }
  };

  return (
    <Card data-testid="vip-followup-queue">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 rounded-lg bg-fuchsia-600 text-white items-center justify-center">
            <Heart size={14} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-[Cormorant_Garamond] text-xl leading-tight">VIP Follow-Up Queue</h3>
            <div className="text-[11px] font-[Outfit] text-emerald-950/60">
              5-star fans + NPS promoters who opted in for a personal follow-up.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-2 py-1 text-[10px] font-[Outfit] font-bold text-fuchsia-800">
              {pending} pending
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-1 text-[10px] font-[Outfit] font-bold text-emerald-800">
              {contacted} contacted
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {vips.length === 0 ? (
          <div className="text-center py-6 text-xs font-[Outfit] text-emerald-950/50" data-testid="vip-empty">
            No VIPs yet. As attendees complete the survey and check &quot;Open to follow-up&quot;, they&apos;ll appear here.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-[11px] font-[Outfit] text-emerald-950/70 flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={showContacted}
                  onChange={(e) => setShowContacted(e.target.checked)}
                  data-testid="vip-show-contacted"
                  className="h-3.5 w-3.5 rounded accent-fuchsia-600"
                />
                Show already-contacted
              </label>
            </div>
            <div className="space-y-2">
              {filtered.map((v) => {
                const att = v.attendee || {};
                const name = `${att.first_name || v.first_name || ""} ${att.last_name || ""}`.trim() || <span className="italic text-emerald-950/50">Anonymous</span>;
                const isContacted = v.followup_status === "contacted";
                return (
                  <div
                    key={v.id}
                    data-testid={`vip-row-${v.id}`}
                    className={`rounded-2xl border-2 p-3 ${isContacted ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-fuchsia-200"}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`inline-flex h-9 w-9 rounded-full items-center justify-center shrink-0 ${isContacted ? "bg-emerald-600 text-white" : "bg-fuchsia-600 text-white"}`}>
                        <Heart size={13} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">{name}</span>
                          <StarCluster n={v.overall_rating} />
                          <NpsPill n={v.recommend_score} />
                          {isContacted && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white font-bold uppercase tracking-widest">Contacted</span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-[Outfit] text-emerald-950/70">
                          {(att.email || v.email) && <span>{att.email || v.email}</span>}
                          {(att.phone || v.phone) && <span>{att.phone || v.phone}</span>}
                          {att.organization && <span className="text-emerald-950/50">· {att.organization}</span>}
                        </div>
                        {(v.favorite_moment || v.improvements) && (
                          <div className="mt-2 space-y-1.5">
                            {v.favorite_moment && (
                              <div className="text-xs text-emerald-950 bg-fuchsia-50 rounded-lg px-2 py-1.5 border border-fuchsia-100">
                                <span className="text-[9px] tracking-widest uppercase font-bold text-fuchsia-700 mr-1">Loved</span>
                                &quot;{v.favorite_moment}&quot;
                              </div>
                            )}
                            {v.improvements && (
                              <div className="text-xs text-emerald-950 bg-emerald-50/70 rounded-lg px-2 py-1.5 border border-emerald-100">
                                <span className="text-[9px] tracking-widest uppercase font-bold text-emerald-700 mr-1">Suggests</span>
                                &quot;{v.improvements}&quot;
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => openThanksModal(v)}
                          data-testid={`vip-thanks-${v.id}`}
                          disabled={!(v.attendee && v.attendee.consent_sms && v.attendee.phone && !v.attendee.unsubscribed)}
                          title={
                            !v.attendee ? "No linked attendee"
                              : !v.attendee.consent_sms ? "Attendee did not opt in to SMS"
                              : v.attendee.unsubscribed ? "Attendee unsubscribed"
                              : !v.attendee.phone ? "No phone on file"
                              : "Send a personalised thank-you text"
                          }
                          className="rounded-full text-[10px] h-8 bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:bg-emerald-100 disabled:text-emerald-800/40"
                        >
                          <Wifi size={11} className="mr-1" /> Send Thanks
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onCopyContact(v)}
                          data-testid={`vip-copy-${v.id}`}
                          className="rounded-full text-[10px] h-8"
                        >
                          <Copy size={11} className="mr-1" /> Copy
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => onMarkContacted(v.id, isContacted ? "pending" : "contacted")}
                          data-testid={`vip-toggle-${v.id}`}
                          className={`rounded-full text-[10px] h-8 ${isContacted ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-900 border border-emerald-300" : "bg-emerald-950 hover:bg-emerald-900 text-white"}`}
                        >
                          {isContacted ? "↩ Reopen" : "✓ Contacted"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
      {thanksModal && (
        <ThanksSmsModal
          state={thanksModal}
          onClose={() => setThanksModal(null)}
          onSend={sendThanks}
          onEdit={(body) => setThanksModal((s) => ({ ...s, body }))}
        />
      )}
    </Card>
  );
}

function ThanksSmsModal({ state, onClose, onSend, onEdit }) {
  const { vip, body = "", can_send, reason, first_name, to_last4, loading, sending } = state;
  const chars = body.length;
  const segments = Math.max(1, Math.ceil(chars / 153));

  return (
    <div
      className="fixed inset-0 z-[60] bg-emerald-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="thanks-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl"
      >
        <div className="px-5 pt-4 pb-2 flex items-start gap-3 border-b border-emerald-100">
          <span className="inline-flex h-10 w-10 rounded-xl bg-fuchsia-600 text-white items-center justify-center shrink-0">
            <Heart size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-700">
              Personal Thank-You SMS
            </div>
            <div className="font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950 mt-0.5">
              Send to {first_name || "this guest"}
              {to_last4 && <span className="text-emerald-950/40 font-[Outfit] text-sm ml-1">···{to_last4}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="thanks-modal-close" aria-label="Close"
            className="rounded-full h-9 w-9 inline-flex items-center justify-center bg-emerald-950/5 hover:bg-emerald-950/10 text-emerald-950 shrink-0">
            <Trash2 size={14} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-sm text-emerald-800/60 py-6 text-center"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading preview…</div>
          ) : !can_send ? (
            <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-3 text-xs text-amber-900" data-testid="thanks-modal-blocker">
              <strong>Cannot send:</strong> {reason || "Not eligible"}. Try Copy contact info instead and call or email.
            </div>
          ) : (
            <>
              <textarea
                data-testid="thanks-modal-body"
                value={body}
                onChange={(e) => onEdit(e.target.value)}
                maxLength={480}
                rows={6}
                className="w-full px-3 py-2 rounded-xl border-2 border-emerald-100 focus:border-fuchsia-500 focus:outline-none font-[Outfit] text-sm bg-white resize-none"
              />
              <div className="flex items-center justify-between text-[10px] font-[Outfit] font-semibold uppercase tracking-widest text-emerald-950/60">
                <span>{chars} chars · {segments} SMS segment{segments > 1 ? "s" : ""}</span>
                <span>Reply STOP included ✓</span>
              </div>
              <div className="rounded-2xl bg-fuchsia-50 border border-fuchsia-100 p-3 text-[11px] font-[Outfit] text-emerald-950/80">
                Auto-marks this VIP as <strong>Contacted</strong> after send. Fires from your Twilio sender <span className="font-mono">+1 (704) 486-2184</span>. Full delivery log in Admin → Event Hub → Overview → SMS.
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  data-testid="thanks-modal-cancel"
                  className="flex-1 rounded-full h-11"
                >Cancel</Button>
                <Button
                  type="button"
                  onClick={onSend}
                  disabled={sending || !body.trim()}
                  data-testid="thanks-modal-send"
                  className="flex-1 rounded-full h-11 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-bold"
                >{sending ? <><Loader2 size={14} className="inline animate-spin mr-1.5" /> Sending…</> : <><Wifi size={14} className="inline mr-1.5" /> Send Thanks</>}</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function NpsPill({ n }) {
  if (n == null || n < 0) return <span className="text-emerald-950/30 text-[11px]">—</span>;
  const tint = n >= 9 ? "bg-emerald-600 text-white" : n >= 7 ? "bg-amber-500 text-white" : "bg-fuchsia-600 text-white";
  return <span className={`inline-flex items-center justify-center rounded-full ${tint} h-6 min-w-[26px] px-2 text-[10px] font-[Outfit] font-bold`}>{n}</span>;
}

function NpsBigPill({ nps }) {
  const v = Number(nps || 0);
  const tint = v >= 50 ? "text-emerald-700" : v >= 0 ? "text-amber-600" : "text-fuchsia-700";
  return <span className={`font-[Outfit] font-bold ${tint}`}>{v > 0 ? "+" : ""}{v}</span>;
}

function StatCardBig({ label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-emerald-100 p-3" data-testid={testid}>
      <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-emerald-800/60">{label}</div>
      <div className="mt-1.5 font-[Cormorant_Garamond] text-2xl leading-tight text-emerald-950">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tint = "emerald", icon = null, testid }) {
  const tints = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    fuchsia: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800",
  };
  return (
    <div className={`rounded-xl border-2 ${tints[tint]} p-2.5`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] tracking-widest uppercase font-[Outfit] font-bold opacity-70">
        {icon}{label}
      </div>
      <div className="mt-0.5 font-[Cormorant_Garamond] text-xl leading-tight">{value}</div>
    </div>
  );
}

function SurveyStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-fuchsia-100 p-2.5">
      <div className="text-[10px] tracking-widest uppercase font-[Outfit] font-bold text-fuchsia-700">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}


// ─── Analytics ──────────────────────────────────────────────────────────────
function AnalyticsSub() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/event/admin/analytics", { params: { days } });
        setData(data);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, [days]);

  const csvEarly = async () => {
    try {
      const res = await fetch(`${API_BASE}/event/admin/early-access.csv`, { headers: { Authorization: `Bearer ${getSessionToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `early_access_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-[Outfit] text-emerald-900">Window:</span>
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`text-xs font-[Outfit] px-3 py-1.5 rounded-full ${days === d ? "bg-emerald-950 text-white" : "bg-white border border-emerald-200 text-emerald-900"}`}>
            {d}d
          </button>
        ))}
        <Button onClick={csvEarly} className="ml-auto rounded-full bg-emerald-950 hover:bg-emerald-900 text-white text-xs h-9">
          <Download size={14} className="mr-1.5" /> Early Access CSV
        </Button>
      </div>
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm font-[Outfit]">
              <thead className="bg-emerald-50 text-emerald-900">
                <tr><th className="text-left px-3 py-2">Event Kind</th><th className="text-right px-3 py-2">Count</th></tr>
              </thead>
              <tbody>
                {Object.entries(data?.counts || {}).length === 0 ? (
                  <tr><td colSpan={2} className="p-6 text-center text-emerald-800/60">No analytics events yet.</td></tr>
                ) : (
                  Object.entries(data?.counts || {}).map(([k, v]) => (
                    <tr key={k} className="border-t border-emerald-100">
                      <td className="px-3 py-2 text-emerald-950 font-mono">{k}</td>
                      <td className="px-3 py-2 text-right font-bold text-emerald-950">{v}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
